export type CloudVariant = 'A' | 'B' | 'C';

export interface CloudPoint {
  x: number;
  y: number;
}

interface CubicSegment {
  control1: CloudPoint;
  control2: CloudPoint;
  end: CloudPoint;
}

interface CloudDefinition {
  width: number;
  height: number;
  start: CloudPoint;
  segments: CubicSegment[];
}

const CLOUDS: Record<CloudVariant, CloudDefinition> = {
  A: {
    width: 220,
    height: 86,
    start: { x: 0, y: 62 },
    segments: [
      { control1: { x: 3, y: 51 }, control2: { x: 20, y: 43 }, end: { x: 44, y: 43 } },
      { control1: { x: 55, y: 19 }, control2: { x: 70, y: 0 }, end: { x: 94, y: 0 } },
      { control1: { x: 117, y: 0 }, control2: { x: 134, y: 13 }, end: { x: 141, y: 31 } },
      { control1: { x: 157, y: 31 }, control2: { x: 166, y: 42 }, end: { x: 182, y: 39 } },
      { control1: { x: 203, y: 40 }, control2: { x: 218, y: 48 }, end: { x: 220, y: 60 } },
      { control1: { x: 220, y: 72 }, control2: { x: 214, y: 80 }, end: { x: 200, y: 80 } },
      { control1: { x: 165, y: 84 }, control2: { x: 128, y: 86 }, end: { x: 95, y: 86 } },
      { control1: { x: 55, y: 86 }, control2: { x: 12, y: 82 }, end: { x: 0, y: 62 } },
    ],
  },
  B: {
    width: 150,
    height: 52,
    start: { x: 0, y: 38 },
    segments: [
      { control1: { x: 5, y: 29 }, control2: { x: 21, y: 25 }, end: { x: 42, y: 25 } },
      { control1: { x: 51, y: 8 }, control2: { x: 64, y: 0 }, end: { x: 78, y: 0 } },
      { control1: { x: 94, y: 1 }, control2: { x: 102, y: 19 }, end: { x: 112, y: 27 } },
      { control1: { x: 133, y: 27 }, control2: { x: 148, y: 31 }, end: { x: 150, y: 37 } },
      { control1: { x: 150, y: 45 }, control2: { x: 142, y: 49 }, end: { x: 132, y: 49 } },
      { control1: { x: 105, y: 52 }, control2: { x: 82, y: 52 }, end: { x: 60, y: 52 } },
      { control1: { x: 31, y: 52 }, control2: { x: 4, y: 48 }, end: { x: 0, y: 38 } },
    ],
  },
  C: {
    width: 185,
    height: 72,
    start: { x: 0, y: 50 },
    segments: [
      { control1: { x: 3, y: 41 }, control2: { x: 18, y: 35 }, end: { x: 34, y: 35 } },
      { control1: { x: 44, y: 12 }, control2: { x: 59, y: 0 }, end: { x: 80, y: 0 } },
      { control1: { x: 94, y: 0 }, control2: { x: 105, y: 5 }, end: { x: 113, y: 11 } },
      { control1: { x: 122, y: 15 }, control2: { x: 125, y: 27 }, end: { x: 134, y: 31 } },
      { control1: { x: 158, y: 31 }, control2: { x: 181, y: 40 }, end: { x: 185, y: 50 } },
      { control1: { x: 185, y: 59 }, control2: { x: 176, y: 66 }, end: { x: 160, y: 66 } },
      { control1: { x: 131, y: 70 }, control2: { x: 100, y: 72 }, end: { x: 78, y: 72 } },
      { control1: { x: 39, y: 72 }, control2: { x: 7, y: 65 }, end: { x: 0, y: 50 } },
    ],
  },
};

const pointOnCubic = (
  start: CloudPoint,
  segment: CubicSegment,
  t: number,
): CloudPoint => {
  const remaining = 1 - t;
  const startWeight = remaining ** 3;
  const control1Weight = 3 * remaining ** 2 * t;
  const control2Weight = 3 * remaining * t ** 2;
  const endWeight = t ** 3;
  return {
    x: startWeight * start.x
      + control1Weight * segment.control1.x
      + control2Weight * segment.control2.x
      + endWeight * segment.end.x,
    y: startWeight * start.y
      + control1Weight * segment.control1.y
      + control2Weight * segment.control2.y
      + endWeight * segment.end.y,
  };
};

export const createCloudContour = (
  variant: CloudVariant,
  centerX: number,
  centerY: number,
  scale: number,
): CloudPoint[] => {
  const cloud = CLOUDS[variant];
  const transform = ({ x, y }: CloudPoint): CloudPoint => ({
    x: centerX + (x - cloud.width / 2) * scale,
    y: centerY + (y - cloud.height / 2) * scale,
  });
  const points = [transform(cloud.start)];
  let segmentStart = cloud.start;
  const samplesPerSegment = 8;
  for (const segment of cloud.segments) {
    for (let sample = 1; sample <= samplesPerSegment; sample += 1) {
      points.push(transform(pointOnCubic(segmentStart, segment, sample / samplesPerSegment)));
    }
    segmentStart = segment.end;
  }
  return points;
};
